import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import {
  FlightsAdvancedFilterType,
  FlightsCabinClass,
  FlightsJourneyCriteriaInput,
  FlightsTravelerDetailsInput,
  FlightsTravelerType,
  FlightsTripType
} from '__generated__/api/types';
import { INFANT_AGE_LIMIT, formatDate } from 'utility';

const logger = ClientLogger.getLoggerWithIdentifier('adapter.searchquery');

const mapAdvancedFilters = (options?: string[]): FlightsAdvancedFilterType[] => {
  if (!options || options.length === 0) {
    return;
  }

  return options
    .map(option => {
      if (option.includes('nopenalty:Y')) {
        return FlightsAdvancedFilterType.REFUNDABLE;
      } else if (option.includes('maxhops:0')) {
        return FlightsAdvancedFilterType.NONSTOP;
      }
    })
    .filter(option => option !== undefined);
};

const mapAirline = (carrierCode?: string): string => {
  return carrierCode && carrierCode.split(':')[1] && carrierCode.split(':')[1] != 'null'
    ? carrierCode.split(':')[1]
    : null;
};

const mapCabinClass = (cabinClassOption?: string): FlightsCabinClass => {
  const cabinClass = cabinClassOption && cabinClassOption.split(':')[1];

  switch (cabinClass) {
    case 'business':
      return FlightsCabinClass.BUSINESS;
    case 'first':
      return FlightsCabinClass.FIRST;
    // To-Do: Remove the 'premium' case below once Storefront
    // makes changes to pass 'premium_economy' in the URL.
    // https://jira.expedia.biz/browse/FFSUAT-92
    case 'premium':
    case 'premium_economy':
      return FlightsCabinClass.PREMIUM_ECONOMY;
    default:
      return FlightsCabinClass.COACH;
  }
};

const mapLeg = (leg: string, locale: string, localDateFormat: string): FlightsJourneyCriteriaInput => {
  try {
    const criteria = decodeURIComponent(leg)
      .replace('from:', '')
      .replace(',to:', '|')
      .replace(',departure:', '|')
      .split('|'); // output [origin, destination, depature]

    return {
      departureDate: criteria[2] ? formatDate(criteria[2], locale, localDateFormat) : '',
      destination: criteria[1] ? criteria[1].split(',toCode')[0] : '',
      origin: criteria[0] ? criteria[0].split(',frCode')[0] : ''
    };
  } catch (error) {
    logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'ENVERROR'), error);

    return null;
  }
};

const parseInfants = (children: number[], infantParameterInUrl: string) => {
  const infants = children.filter(child => child <= INFANT_AGE_LIMIT);

  if (infants.length === 0) {
    return;
  }

  let travelerType = FlightsTravelerType.INFANT_IN_LAP;
  if (infantParameterInUrl && infantParameterInUrl.split(':')[1] === 'N') {
    travelerType = FlightsTravelerType.INFANT_IN_SEAT;
  }

  return {
    ages: infants,
    count: infants.length,
    travelerType
  };
};

const parseChildren = (childrenArray: number[], childCount: number) => {
  const childAges = childrenArray.filter(child => child > INFANT_AGE_LIMIT);
  const remainingChildrenCount = childCount - childAges.length;

  for (let i = 0; i < remainingChildrenCount; i++) {
    childAges.push(-1);
  }

  return {
    ages: childAges,
    count: childCount,
    travelerType: FlightsTravelerType.CHILD
  };
};

const parseAdultsFromUrlString = (adults: string): FlightsTravelerDetailsInput => {
  const adultsParameterArray = adults.split(':');
  const adultCount = parseInt(adultsParameterArray[1], 10);
  const adultsCriteria = {
    travelerType: FlightsTravelerType.ADULT,
    count: 1
  };

  if (!isNaN(adultCount)) {
    adultsCriteria.count = adultCount;
  }

  return adultsCriteria;
};

const getChildCountFromParamString = (children: string) => {
  const childParameterArray = children.split('[');

  if (childParameterArray[0] && parseInt(childParameterArray[0], 10)) {
    return parseInt(childParameterArray[0], 10);
  }
};

const getChildrenAgesFromParamString = (children: string): number[] => {
  const childrenParameterArray = children.split('[');

  if (childrenParameterArray.length !== 2) {
    return [];
  }

  const childrenAgesString = childrenParameterArray[1].replace(']', '');
  const childreAgesArray = childrenAgesString.split(';');

  return childreAgesArray.map(i => parseInt(i, 10));
};

const mapTravelers = (travelersInUrl: string[]): FlightsTravelerDetailsInput[] => {
  const travelers = [];
  const adultParameterFromUrl: string = travelersInUrl.find(traveler => traveler.includes('adults'));
  const childrenParameterFromUrl: string = travelersInUrl.find(traveler => traveler.includes('children'));

  if (adultParameterFromUrl) {
    const adults = parseAdultsFromUrlString(adultParameterFromUrl);

    if (adults) {
      travelers.push(adults);
    }
  }

  if (!childrenParameterFromUrl) {
    return travelers;
  }

  const childrenParametersArray = childrenParameterFromUrl.split(':');

  if (childrenParametersArray.length !== 2 || childrenParametersArray[1] === '0' || childrenParametersArray[1] === '') {
    return travelers;
  }

  const childrenCountAndAges: string = childrenParametersArray[1];
  let childrenCount = getChildCountFromParamString(childrenCountAndAges);

  if (!childrenCount || childrenCount === 0) {
    return travelers;
  }

  const infantParameterInUrl: string = travelersInUrl.find(traveler => traveler.includes('infantinlap'));
  const childrenAges = getChildrenAgesFromParamString(childrenCountAndAges);
  const infants = parseInfants(childrenAges, infantParameterInUrl);

  if (infants) {
    childrenCount = childrenCount - infants.count;
    travelers.push(infants);
  }

  if (childrenCount === 0) {
    return travelers;
  }

  const children = parseChildren(childrenAges, childrenCount);

  if (children) {
    travelers.push(children);
  }

  return travelers;
};

const mapTripType = (tripType: string): FlightsTripType => {
  switch (tripType) {
    case 'oneway':
      return FlightsTripType.ONE_WAY;
    case 'roundtrip':
      return FlightsTripType.ROUND_TRIP;
    case 'multi':
      return FlightsTripType.MULTI_DESTINATION;
    default:
      // TODO: redirect to storefront
      // this allows page to load without trip param
      return FlightsTripType.ONE_WAY;
  }
};

export interface SearchQueryParams {
  leg1?: string;
  leg2?: string;
  leg3?: string;
  leg4?: string;
  leg5?: string;
  options?: string;
  passengers?: string;
  trip?: string;
}

export const SearchQueryAdapter = {
  mapAdvancedFilters,
  mapAirline,
  mapCabinClass,
  mapLeg,
  mapTravelers,
  mapTripType
};
